import React, { useState, useEffect, useRef } from 'react';
import { faBell, faCalendar, faPlus, faTimes} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from 'react-i18next';
import './AddTask.css';
import { Button } from "../button/Button";
import {
    generateId, autoResizeTextarea, formatDateTimeDisplay, handleShiftEnter,
    keepElementVisible, keepFocusedControlVisibleOnTouch,
} from "../../utils/Utils";
import { Element, Habit, Task, Timer, Stopwatch, TimeTracker } from "../../types";
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { ALL_TRACKED_DAYS } from "../../utils/habitUtils";
import { TaskScheduleModal } from '../task-schedule-modal/TaskScheduleModal';
import { formatTaskScheduleLabel } from '../../utils/taskScheduleLabel';
import { withScheduleStartDate } from '../../utils/taskSchedule';

interface AddElementProps {
    addElement: (element: Element) => void;
    visibleTypes: string[];
    initiallyExpanded?: boolean;
    autoExpandKey?: string;
    draftKey?: string;
    hideCollapsedButton?: boolean;
    onCollapse?: () => void;
}

export const AddElement: React.FC<AddElementProps> = ({
    addElement,
    visibleTypes,
    initiallyExpanded = false,
    autoExpandKey,
    draftKey,
    hideCollapsedButton = false,
    onCollapse,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState('');
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
    const [dueDate, setDueDate] = useState<Dayjs | null>(null);
    const [schedule, setSchedule] = useState<Task['schedule']>();
    const [reminderTime, setReminderTime] = useState<Dayjs | null>(null);
    const [recurrence, setRecurrence] = useState<Task['recurrence']>();
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const optionsWereVisibleRef = useRef(false);
    
    // Determine the element type to add based on visible types
    const elementType = visibleTypes.includes('task') ? 'task' : visibleTypes[0] || 'task';
    const draftStorageKey = draftKey
        ? `minderBox:addElementDraft:${draftKey}:${elementType}`
        : undefined;
    const legacyDraftStorageKey = draftKey
        ? `easyDone:addElementDraft:${draftKey}:${elementType}`
        : undefined;
    
    useEffect(() => {
        if (textareaRef.current) {
            autoResizeTextarea({ target: textareaRef.current });
        }
    }, [value]);

    useEffect(() => {
        const optionsAreVisible = elementType === 'task' && Boolean(value);
        if (!optionsAreVisible) {
            optionsWereVisibleRef.current = false;
            return;
        }

        if (!optionsWereVisibleRef.current && formRef.current) {
            optionsWereVisibleRef.current = true;
            keepElementVisible(formRef.current);
        }
    }, [elementType, value]);

    useEffect(() => {
        if (isExpanded) {
            textareaRef.current?.focus();
            if (textareaRef.current) keepFocusedControlVisibleOnTouch(textareaRef.current);
        }
    }, [isExpanded]);

    useEffect(() => {
        if (initiallyExpanded) {
            setIsExpanded(true);
        }
    }, [autoExpandKey, initiallyExpanded]);

    useEffect(() => {
        if (!draftStorageKey) return;

        const savedDraft = window.localStorage.getItem(draftStorageKey)
            || (legacyDraftStorageKey && window.localStorage.getItem(legacyDraftStorageKey))
            || '';
        setValue(savedDraft);

        if (savedDraft && legacyDraftStorageKey) {
            window.localStorage.setItem(draftStorageKey, savedDraft);
            window.localStorage.removeItem(legacyDraftStorageKey);
        }

        if (savedDraft.trim()) {
            setIsExpanded(true);
        }
    }, [draftStorageKey, legacyDraftStorageKey]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (handleShiftEnter(e, value, handleValueChange)) {
            return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!value) {
            return;
        }

        handleAddElement();
        resetForm();
        setIsExpanded(true);
    };

    const resetForm = () => {
        setValue("");
        if (draftStorageKey) {
            window.localStorage.removeItem(draftStorageKey);
        }
        setDueDate(null);
        setSchedule(undefined);
        setReminderTime(null);
        setRecurrence(undefined);
        setShowScheduleDialog(false);
        window.setTimeout(() => {
            if (!textareaRef.current) return;
            textareaRef.current.focus();
            keepFocusedControlVisibleOnTouch(textareaRef.current);
        }, 0);
    };

    const handleCancelTask = () => {
        resetForm();
        setIsExpanded(false);
        onCollapse?.();
    };

    const handleValueChange = (nextValue: string) => {
        setValue(nextValue);

        if (!draftStorageKey) return;

        if (nextValue) {
            window.localStorage.setItem(draftStorageKey, nextValue);
        } else {
            window.localStorage.removeItem(draftStorageKey);
        }
    };

    const handleAddElement = () => {
        let newElement: Element;

        if (elementType === 'task') {
            newElement = {
                id: generateId(),
                type: 'task',
                title: value,
                completed: false,
                edit: false,
                dueDate: dueDate ? dueDate.toISOString() : recurrence ? dayjs().startOf('day').toISOString() : undefined,
                schedule: schedule || recurrence
                    ? withScheduleStartDate(schedule || { precision: 'day' }, dueDate ? dueDate.toISOString() : dayjs().startOf('day').toISOString())
                    : undefined,
                reminderTime: reminderTime ? reminderTime.toISOString() : undefined,
                recurrence,
            };
        } else if (elementType === 'note') {
            newElement = {
                id: generateId(),
                type: 'note',
                title: value.split('\n')[0].trim() || t('addTask.untitledNote'),
                content: value,
                lastUpdated: new Date().toISOString(),
            };
        } else if (elementType === 'habit') {
            newElement = {
                id: generateId(),
                type: 'habit',
                title: value,
                frequency: 'daily',
                trackedDays: ALL_TRACKED_DAYS,
                count: undefined,
                unit: undefined,
                completedDates: [],
            } as Habit;
        } else if (elementType === 'timer') {
            newElement = {
                id: generateId(),
                type: 'timer',
                title: value,
                mode: 'simple',
                subtimers: [{ id: generateId(), text: value, targetTime: 60, timeHistory: [], completed: false }],
                completedDates: [],
            } as Timer;
        } else if (elementType === 'stopwatch') {
            newElement = {
                id: generateId(),
                type: 'stopwatch',
                title: value,
                mode: 'simple',
                steps: [{ id: generateId(), text: value, timeHistory: [], completed: false }],
                completedDates: [],
                sessionHistory: [],
            } as Stopwatch;
        } else if (elementType === 'timeTracker') {
            newElement = {
                id: generateId(),
                type: 'timeTracker',
                title: value,
                activities: [],
                segments: [],
            } as TimeTracker;
        } else {
            // Default case - shouldn't reach here but TypeScript needs this
            newElement = {
                id: generateId(),
                type: 'task',
                title: value,
                completed: false,
                edit: false,
            };
        }

        addElement(newElement);
    };

    const handleBlur = () => {
        if (!value.trim()) {
            setIsExpanded(false);
            onCollapse?.();
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form ref={formRef} onSubmit={handleSubmit} className={"todo-add-step-container"}>
                {!isExpanded ? (
                    hideCollapsedButton ? null : (
                    <button
                        type="button"
                        className="add-task-collapsed-button"
                        onClick={() => setIsExpanded(true)}
                    >
                        <span className="add-task-collapsed-icon">
                            <FontAwesomeIcon icon={faPlus} />
                        </span>
                        {t(
                            `addTask.add${elementType.charAt(0).toUpperCase() + elementType.slice(1)}Label`,
                        )}
                    </button>
                    )
                ) : (
                    <div className="input-button-row">
                        <textarea
                            ref={textareaRef}
                            placeholder={t(
                                `addTask.add${elementType.charAt(0).toUpperCase() + elementType.slice(1)}`,
                            )}
                            value={value}
                            onChange={(e) => handleValueChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            rows={elementType === "note" ? 3 : 1}
                            className={`formatted-input ${elementType === 'task' && !value ? 'empty-task-input' : ''}`}
                        />
                        {elementType === 'task' && !value && (
                            <button
                                type="button"
                                className="add-task-input-close"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={handleCancelTask}
                                aria-label={t('addTask.closeInput')}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                )}
                {elementType === "task" && value && (
                    <div className="task-options-row">
                        <div className="option-group-left">
                            <button type="button" className={`option-icon ${dueDate || reminderTime || recurrence ? 'active' : ''}`} onClick={() => setShowScheduleDialog(true)} aria-label="Task schedule">
                                <FontAwesomeIcon icon={faCalendar} />
                            </button>
                            {dueDate && (
                                <button
                                    type="button"
                                    className="task-schedule-inline-label"
                                    onClick={() => setShowScheduleDialog(true)}
                                    aria-label="Edit task schedule"
                                >
                                    {formatTaskScheduleLabel(dueDate.toDate(), schedule)}
                                </button>
                            )}
                            {reminderTime && (
                                <button
                                    type="button"
                                    className="task-reminder-inline-label"
                                    onClick={() => setShowScheduleDialog(true)}
                                    aria-label="Edit task schedule"
                                >
                                    <FontAwesomeIcon icon={faBell} />
                                    {formatDateTimeDisplay(reminderTime.toISOString())}
                                </button>
                            )}
                        </div>
                        <div className="option-group-right">
                            <Button
                                onClick={handleCancelTask}
                                type={"button"}
                                status={"secondary"}
                                text={""}
                                icon={faTimes}
                                ariaLabel="Cancel add subtask"
                            />
                            <Button
                                onClick={handleSubmit}
                                type={"button"}
                                status={"primary"}
                                text={""}
                                icon={faPlus}
                                ariaLabel="Add element"
                            />
                        </div>
                    </div>
                )}
                {showScheduleDialog && <TaskScheduleModal dueDate={dueDate} schedule={schedule} reminderTime={reminderTime} recurrence={recurrence}
                    onDueDateChange={setDueDate} onScheduleChange={setSchedule} onReminderTimeChange={setReminderTime} onRecurrenceChange={setRecurrence}
                    onClose={() => setShowScheduleDialog(false)} />}
            </form>
        </LocalizationProvider>
    );
};
